import React, { useEffect, useRef, useState } from 'react'
import QRCode from 'qrcode'
import { animated, useSpring } from '@react-spring/web'
import { osName, browserName, OsTypes, BrowserTypes } from 'react-device-detect'
import { useSelector } from 'react-redux'
import { RootState } from '../../../../store'

import { docsRetorik } from '../../../../models/constants'

import { MicrophoneRefusedIcon } from '../../../Icons/Miscellaneous'
import QRCodeComponent from '../../../Utils/QRCodeComponent'
import CloseButton from '../../../Common/CloseButton'
import { RoundIcon } from '../../../Icons/DetailedPOIIcons'

const springDuration = 500

const MicrophoneModalRefused = (): JSX.Element => {
  const locale = useSelector((state: RootState) => state.localeReducer.locale)
  const translation = useSelector((state: RootState) => state.localeReducer.currentTranslations)
  const isUsedOnBorne = useSelector((state: RootState) => state.retorikReducer.configuration.isUsedOnBorne)
  const [qrCodeData, setQrCodeData] = useState<string | undefined>(undefined)
  const [redirectionUrl, setRedirectionUrl] = useState<string>()
  const fadeInTimerRef = useRef<NodeJS.Timer | null>(null)

  const [spring, api] = useSpring(() => ({
    from: {
      opacity: 0
    }
  }))

  useEffect(() => {
    let tempUrl = `${docsRetorik}${locale.split('-')[0] === 'fr' ? '' : '/en'}/docs/depannage-autorisations-acces-micro/`

    switch (browserName) {
      case BrowserTypes.Chrome:
      case BrowserTypes.Chromium:
        tempUrl += '#chrome-'
        break
      case BrowserTypes.Edge:
      case BrowserTypes.EdgeChromium:
        tempUrl += '#edge-'
        break
      case BrowserTypes.Firefox:
        tempUrl += '#firefox-'
        break
      case BrowserTypes.Safari:
      case BrowserTypes.MobileSafari:
        tempUrl += '#safari-'
        break
      case BrowserTypes.Opera:
        tempUrl += '#opera-'
        break
      case BrowserTypes.SamsungBrowser:
        setRedirectionUrl(`${tempUrl}#samsung-sur-mobile`)
        return
      case BrowserTypes.Ie:
      case BrowserTypes.InternetExplorer:
      default:
        break
    }

    switch (osName) {
      case OsTypes.Windows:
      case OsTypes.MAC_OS:
        tempUrl += 'sur-ordinateur-de-bureau'
        break
      default:
        tempUrl += 'sur-mobile-ou-tablette'
    }

    setRedirectionUrl(tempUrl)
  }, [locale])

  useEffect(() => {
    qrCodeData &&
      api.start({
        from: {
          opacity: 0
        },
        to: {
          opacity: 1
        },
        config: {
          duration: springDuration
        }
      })

    return () => {
      fadeInTimerRef?.current && clearTimeout(fadeInTimerRef.current)
    }
  }, [qrCodeData])

  const handleCloseQrCode = (): void => {
    api.start({
      from: {
        opacity: 1
      },
      to: {
        opacity: 0
      },
      config: {
        duration: springDuration
      }
    })

    fadeInTimerRef?.current && clearTimeout(fadeInTimerRef.current)
    fadeInTimerRef.current = setTimeout(() => {
      setQrCodeData(undefined)
    }, springDuration)
  }

  const setQrCodeDataAsync = async (url: string): Promise<void> => {
    const qrCodeString = await QRCode.toDataURL(url)
    setQrCodeData(qrCodeString)
  }

  const handleRedirect = (): void => {
    if (redirectionUrl) {
      isUsedOnBorne ? setQrCodeDataAsync(redirectionUrl) : window.open(redirectionUrl, '_blank', 'noopener noreferrer')
    }
  }

  return (
    <div className='rf-max-w-[500px] rf-px-8 rf-py-8 rf-flex rf-flex-col rf-justify-center rf-items-center rf-gap-4 rf-text-size-auto'>
      <MicrophoneRefusedIcon className='rf-h-10' />
      <p className='rf-font-light'>{translation.microphone.rejected}</p>
      <p className='rf-text-center'>{translation.microphone.instructions}</p>

      <button className='rf-card-button' onClick={handleRedirect}>
        {translation.common.knowmore}
      </button>

      {/* QR code */}
      {qrCodeData && (
        <animated.div
          className='rf-absolute rf-h-full rf-w-full rf-top-0 rf-left-0 rf-z-ui rf-flex rf-flex-col rf-justify-center rf-items-center rf-gap-2 rf-p-2 rf-text-truewhite'
          style={{
            background: 'rgba(18,18,18,0.7)',
            ...spring
          }}
        >
          <CloseButton onClick={handleCloseQrCode} showLabel={false} />

          {/* Icon + Text */}
          <div className='rf-px-8 rf-flex rf-flex-row rf-items-start rf-gap-1 rf-italic'>
            <RoundIcon color='#FFF' />
            <div className='rf-w-fit'>{translation.link.scanQRTile}</div>
          </div>

          {/* QR code */}
          <QRCodeComponent src={qrCodeData} />
        </animated.div>
      )}
    </div>
  )
}

export default MicrophoneModalRefused
